import { AxiosResponse } from "axios"

import { SummaryQueryType, WorkbookSuggestionOption } from "@store/search/types"
import {
  AnswerViewType,
  QuestionFeedbackType,
} from "@framework/constants/search-results"
import {
  AnswerData,
  AnswerSourceMetaData,
  SearchFilterType,
} from "@framework/types/search"
import { SearchMode } from "@framework/types/search-mode"
import { Product } from "@framework/types/product"
import { Company } from "@framework/types/company"
import { WorkbookReportEntity } from "@framework/types/workbook-report"

import { DefaultSuccessResponse } from "./common/types"
import HttpService, { StreamEvents } from "./http.service"

type SearchStreamMeta = {
  id: string
  queryType?: SummaryQueryType
  threadId?: string
}

export type StreamSearchResult = {
  type: "SEARCH_RESULT"
  message: null
  data: {
    searchResults: AnswerResponseData
  }
  meta?: SearchStreamMeta
}

export type StreamSearchStatus = {
  type: "STATUS_MESSAGE" | "ERROR"
  message: string
  data: null
  meta?: SearchStreamMeta
}

export type StreamSearchSummary = {
  type: "SUMMARY"
  message: string | null
  data: {
    summary: {
      confidence: number
      keyInfo: string
      keyInfoList: string[]
      summary: string
      // or
      text: string
      source: string
      questionId?: string
      attachmentTruncated?: boolean
    }
  }
  meta?: SearchStreamMeta
}

export type StreamSearchSummaryDelta = {
  type: "SUMMARY_DELTA"
  message: string | null
  data: {
    text: string
    source?: string
  }
  meta?: SearchStreamMeta
}

export type StreamAutogeneratedReport = {
  type: "WORKBOOK_REPORT_MESSAGE"
  message: string | null
  data: { reportData: WorkbookReportEntity }
  meta?: SearchStreamMeta
}

export type StreamSuggestedQuestion = {
  type: "SUGGESTED_QUESTIONS"
  message: string | null
  data: {
    suggested_questions: string[]
    source?: string
  }
  meta?: SearchStreamMeta
}

export type AllCitationContext = {
  type: "ALL_CITATION_CONTEXTS"
  message: string | null
  data: {
    citationContexts?: AnswerCitationDetails[]
  }
  meta?: SearchStreamMeta
}

export type StreamMessage =
  | StreamSearchResult
  | StreamSearchStatus
  | StreamSearchSummary
  | StreamSearchSummaryDelta
  | StreamSuggestedQuestion
  | StreamAutogeneratedReport
  | AllCitationContext

export type AnswerCitationDetails = {
  citationKey: number
  relevantText: string
  summaryText: string
  citationId: string
}

export type AnswerResponseData = {
  answer: {
    answers: AnswerData[]
  }
  filesInfo: AnswerSourceMetaData[]
  filesWithHit: {
    [key: string]: AnswerSourceMetaData
  }
}

export interface GetSourceInfoResponse {
  data: {
    source: string
    file_size: number
  }[]
  message: string
  status: "SUCCESS"
}

export interface GetSuggestionsResponse extends DefaultSuccessResponse {
  data: string[]
}

export interface UpdateAnswerUpVote extends DefaultSuccessResponse {
  data: {
    current_user_vote: 1 | 0
  }
}

export type SendQuestionFeedbackResponse = DefaultSuccessResponse

export interface UpdateAnswerFeedbackRequestBody {
  question: string
  answer: string
  userFeedback: QuestionFeedbackType
  comment: string
  summaryType?: string
  productSolutionId?: string
  rating?: number
}

export interface UpdateReportFeedbackRequestBody {
  reportId: string
  comment: string
  rating: number
}

export interface GetQueryHighlightsResponse {
  products: Product[]
  companies: Company[]
}

export interface GetQueryWorkbookSuggestionsResponse {
  workflowSuggestions: WorkbookSuggestionOption[]
}

export type AnswerSnapshotEvent = {
  type: string
  message: string | null
  data: any
  meta?: SearchStreamMeta
}

export interface QuestionAnswerSnapshot {
  id: string
  question: string
  summary: string
  citations: string
  createdAt: string
  hasAnswerSnapshot: boolean
  events: AnswerSnapshotEvent[]
}

class SearchAPI extends HttpService {
  getUnstructuredSearchStream = (
    filter: SearchFilterType,
    tools: {
      withWebResearch?: boolean
      searchMode?: SearchMode
      answerFormat?: string
    },
    events: StreamEvents,
    signal?: AbortSignal
  ) =>
    this.postJSONStream(
      "ts/v2/integrated-query",
      { ...filter, ...tools, streamFlag: true },
      events,
      { auth: true, signal }
    )

  getFilesInfo = (
    filesInfo: { source: string }[]
  ): Promise<AxiosResponse<GetSourceInfoResponse>> =>
    this.post("ts/query/files-info", { filesInfo })

  getUsersQuestions = (
    avatar: string,
    productSolutionId: string
  ): Promise<AxiosResponse<GetSuggestionsResponse>> =>
    this.get(`ts/${avatar}/question/my-questions`, true, { productSolutionId })

  sendAnswerFeedback = (
    request: UpdateAnswerFeedbackRequestBody
  ): Promise<AxiosResponse<SendQuestionFeedbackResponse>> =>
    this.post("ts/question/user-feedback", request)

  sendReportFeedback = (
    request: UpdateReportFeedbackRequestBody
  ): Promise<AxiosResponse<SendQuestionFeedbackResponse>> =>
    this.post("ts/companies/workbook-reports/feedback", request)

  upVoteAnswer = (
    question: string,
    answer: AnswerData,
    answerType: AnswerViewType,
    avatar: string,
    avatarId: string
  ): Promise<AxiosResponse<UpdateAnswerUpVote>> =>
    this.post("ts/v2/vote", {
      question,
      answer,
      answerType,
      avatarName: avatar,
      avatarId,
    })

  getQueryHighlights = (
    summary: string,
    questionId: string
  ): Promise<AxiosResponse<GetQueryHighlightsResponse>> =>
    this.post(
      "ts/v2/integrated-query/highlights",
      {
        summary,
        questionId,
      },
      true
    )

  getWorkbookSuggestionsByQuery = (
    query: string,
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetQueryWorkbookSuggestionsResponse>> =>
    this.get(
      "ts/v2/integrated-query/workflow-suggestions",
      true,
      {
        query,
      },
      null,
      signal
    )

  getQuestionSnapshot = (
    questionId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse<QuestionAnswerSnapshot>>> =>
    this.get(`ts/question/${questionId}/snapshot`, true)
}

export default new SearchAPI()
